// Korean dictionary — source of truth for keys.
// Values may contain HTML (used via data-i18n-html or t() into innerHTML)
// and {param} placeholders interpolated by t().
export const ko: Record<string, string> = {
  // ── meta ──
  'meta.title': 'DigMol — Dener 서임 · 엘도라시아 연대기',

  // ── topbar / wallet ──
  'topbar.become': 'Dener 서임 받기',
  'wallet.connect': '지갑 연결',
  'wallet.connected': '연결됨',
  'wallet.copy': '복사',
  'wallet.copied': '복사됨!',
  'wallet.myMol': '내 MOL',
  'wallet.denerDash': '내 총판 대시보드',
  'wallet.admin': '관리자',
  'wallet.disconnect': '연결 해제',
  'wallet.roleOwner': 'OWNER',
  'wallet.roleDist': '총판',
  'wallet.connectCancelled': '지갑 연결이 취소되었습니다.',
  'wallet.noWalletFallback': '지갑을 연결할 수 없습니다. 잠시 후 다시 시도하세요.',
  'wallet.connectFail': '연결 실패: {msg}',

  // ── 모바일 지갑 연결 선택 (딥링크 / WalletConnect) ──
  'wallet.mobileGuide': '지갑을 선택해 연결합니다. MetaMask 도 여기서 연결됩니다.',
  'wallet.openInMetaMask': 'MetaMask 앱에서 열기 (보조)',
  'wallet.retryMetaMask': 'MetaMask 앱에서 다시 열기',
  'wallet.useMetaMaskApp':
    'MetaMask 앱이 이미 열려 있을 수 있습니다. 앱으로 전환해 진행하거나, 아래에서 다시 시도해 주세요.',
  'wallet.otherWallet': '다른 지갑 연결',
  'wallet.cancel': '닫기',
  'wallet.stuckNotice': '지갑이 응답하지 않습니다. 새로고침하거나 MetaMask 앱을 재시작해 주세요.',
  'wallet.reload': '새로고침',
  'wallet.connectWc': '지갑 연결하기',
  'wallet.preparing': '준비 중…',
  'wallet.deadSession': '지갑 세션이 만료되었습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.',

  // ── 체인(네트워크) 경고 ──
  'chain.wrong': '네트워크가 BNB Smart Chain 이 아닙니다. 전환해야 이용할 수 있습니다.',
  'chain.switch': 'BSC 로 전환',

  // ── 인앱 브라우저(카톡/라인/인스타…) 외부 브라우저 안내 ──
  'inapp.title': '{app} 에서는 지갑을 연결할 수 없습니다',
  'inapp.desc': '보안상 인앱 브라우저는 지갑 앱을 열 수 없습니다. 외부 브라우저로 열면 바로 연결됩니다.',
  'inapp.open': '외부 브라우저로 열기',
  'inapp.copy': '주소 복사',
  'inapp.copied': '주소를 복사했습니다. 브라우저에 붙여넣어 주세요.',
  'inapp.copyFail': '복사에 실패했습니다. 주소창을 길게 눌러 복사해 주세요.',
  'inapp.manualOnly': '자동 열기가 지원되지 않습니다. 주소를 복사한 뒤 브라우저에 붙여넣어 주세요.',
  'inapp.justBrowse': '그냥 둘러보기',
  'wallet.deeplinkFailed':
    'MetaMask 앱이 열리지 않았거나 연결되지 않았습니다. 아래 [지갑 연결하기] 로 진행해 주세요.',

  // ── language toggle ──
  'lang.toggleAria': '언어 전환',

  // ── hero / surface ──
  'alt.mole': 'MolePin 두더지',
  'hero.headline': '무너진 왕국을<br><em>다시 지킬 자</em>를 부른다.',
  'hero.tagline': 'Eldorasia calls for those who will guard it again.',
  'hero.lead':
    '열일곱 황금 두더지 부족의 지하 왕국 <b>엘도라시아</b>. 열여섯이 사라진 뒤, 마지막 후예 몰핀이 다시 굴을 판다. <b>Dener</b>는 그 곁에서 광맥을 지키는 수호자 리더 — 그리고 그 서임은, 충분한 <b>MOL</b>을 품는 데서 시작된다.',
  'hero.scrollcue': '광맥으로 내려가 서임을 받으세요',

  // ── lore (section 1) ──
  'lore.eyebrow': '제1층 · Dener란 누구인가',
  'lore.headline': '파는 자가 아니라,<br><em>지키는 자</em>다.',
  'lore.quote': '“광맥을 캐는 손은 많으나, 그것을 지키는 자는 드물다.”',
  'lore.lead':
    'Dener는 MOL 생태계에 기여하며 그 가치를 증명한 <b>수호자 리더</b>다. 두더지 몰핀이 동족의 영혼으로 사슬을 잇듯, Dener는 충분한 MOL을 품어 광맥의 안정을 떠받친다. 이 자리에 오르는 절차가 바로 <b style="color:var(--gold)">PV</b> — Dener 서임이다.',
  'lore.pillar1.title': '수호의 자격',
  'lore.pillar1.desc': '2천만 MOL을 품은 자만이 Dener로 서임된다. 자격은 권리가 아니라 책임이다.',
  'lore.pillar1.en': '권리가 아닌, 수호.',
  'lore.pillar2.title': '증명된 기여',
  'lore.pillar2.desc': '서임은 온체인에 새겨진다. 누구도 흉내 낼 수 없고, 누구나 검증할 수 있다.',
  'lore.pillar2.en': '온체인에 증명되고, 누구나 검증한다.',
  'lore.pillar3.title': '열린 광맥',
  'lore.pillar3.desc': '정해진 비율대로, 넣은 만큼 정확히 캔다. 흥정도 차별도 없다.',
  'lore.pillar3.en': '고정 비율. 흥정 없음.',

  // ── steps (section 2) ──
  'steps.eyebrow': '제2층 · 서임의 길',
  'steps.headline': '네 걸음이면,<br><em>수호자</em>가 된다.',
  'steps.tagline': 'Four steps to become a Dener.',
  'steps.s1.t': '굴 입구에 선다 — 지갑 연결',
  'steps.s1.d': 'MetaMask 또는 호환 지갑으로 BSC에 접속한다. 모바일은 지갑 앱의 내장 브라우저에서 연다.',
  'steps.s2.t': '광맥에 BNB를 넣는다',
  'steps.s2.d': '교환할 BNB를 입력하면, 계약에 새겨진 비율로 캐낼 MOL이 즉시 계산된다.',
  'steps.s3.t': '곡괭이를 휘두른다 — 서명',
  'steps.s3.d': '서명 한 번. 컨트랙트가 비율 그대로 MOL을 지갑으로 보낸다.',
  'steps.s4.t': '서임의 빛이 차오른다',
  'steps.s4.d': '품은 MOL이 2천만에 닿는 순간, Dener의 자격이 열린다.',

  // ── oath (section 3) ──
  'oath.eyebrow': '제3층 · 광맥의 맹세',
  'oath.quote':
    '“계약은 흥정하지 않는다.<br>넣은 만큼, 새겨진 비율대로 — 그뿐이다.”<span class="src">— THE VEIN, ELDORASIA</span>',
  'oath.lead':
    '이 서임의 광맥은 생태계의 다른 어떤 계약과도 얽혀 있지 않다. 비율 하나, 재고 하나, 그리고 너의 서명. 그래서 따로, 안전하게, 검증할 수 있다.',

  // ── vein (section 4) ──
  'vein.eyebrow': '최심부 · THE VEIN',
  'vein.headline': '광맥에 닿았다.',
  'vein.tagline': 'Where BNB becomes MOL. Where a guardian is made.',
  'panel.title': 'Dener <em>서임의 광맥</em>',
  'panel.subtitle': 'PV INVESTITURE · ISOLATED VEIN',
  'panel.gateLabel': 'Dener 서임까지 — 품은 광맥',
  'panel.gateLine': '서임선 <b>20,000,000 MOL</b>',

  // ── swap box ──
  'swap.bnbLabel': '광맥에 넣을 BNB',
  'swap.molOutLabel': '캐낼 MOL (계약 비율)',
  'swap.rateNote': '비율은 컨트랙트에 새겨진 값으로 계산됩니다.',
  'swap.statRate': '계약 비율',
  'swap.statStock': '남은 광맥',
  'swap.statMol': '내 MOL',
  'swap.contractMeta': '컨트랙트 주소를 설정하면 실시간 비율·재고가 표시됩니다.',
  'cta.mine': '곡괭이를 휘두른다 — MOL 캐기',

  // ── status / investiture (shared by panel & modal) ──
  'status.pending': '○ 미서임',
  'status.dener': '● Dener 서임',
  'inv.done': '⛏ <b>Dener로 서임되었습니다.</b> 광맥의 수호자 자격을 갖추었습니다.',
  'inv.remaining': '서임까지 <b>{amount} MOL</b> 더 품어야 합니다.',

  // ── become-a-dener modal ──
  'modal.title': 'Dener 서임 <em>의식</em>',
  'modal.subtitle': 'BECOME A DENER · BNB → MOL',
  'modal.close': '닫기',
  'modal.gateLabel': 'Dener 서임까지',
  'modal.molRangeLabel': '구매할 MOL 수량',
  'modal.molRangeOpt': '(최소 2천만)',
  'modal.tick1': '2천만',
  'modal.tick2': '1억',
  'modal.tick3': '2억',
  'modal.payLabel': '지불할 BNB',
  'modal.priceLoading': '가격을 불러오는 중…',
  'modal.distLabel': '총판 선택',
  'modal.distOpt': '(등록된 총판)',
  'modal.distPlaceholder': '예: alice.dex',
  'modal.outLabel': '받을 MOL',
  'modal.outNote': '총판을 선택하면 할인된 수량이 표시됩니다.',
  'modal.noAddrMeta': '⚠ PV_GATE_ADDRESS 미설정 — .env 에 VITE_PV_GATE_ADDRESS 필요',
  'modal.priceNoteNoAddr': '컨트랙트 주소 설정 후 가격이 표시됩니다.',
  'modal.priceNote': 'MOL {mol} · BNB {bnb} (피드)',
  'modal.priceFail': '가격을 불러오지 못했습니다.',
  'modal.needMolAmount': '구매할 MOL 수량을 설정하세요.',
  'modal.noAddrErr': 'PV_GATE_ADDRESS가 설정되지 않았습니다.',
  'modal.discountChip': '<span class="dk">총판 할인 (+{pct}%)</span><span class="dv">+{extra} MOL</span>',
  'modal.rateBase': '기본 {base} MOL + 할인 {extra} MOL',
  'modal.rewardLine': '<br>총판 보상 {rwd} BNB 자동 지급',
  'modal.noDiscountReward': '할인 없음 · 총판 보상 {rwd} BNB 자동 지급',
  'modal.distHint': '총판을 선택하면 할인/보상이 표시됩니다.',

  // ── 구매자 대표 도메인 안내 (역주소조회) ──
  'domain.connectFirst': '지갑 연결을 먼저 진행해 주세요.',
  'domain.checking': '지갑 {addr} 의 대표 도메인 확인 중…',
  'domain.checkedWallet': '확인한 지갑: {addr}',
  'domain.noneTitle': '이 지갑에 연결된 대표 도메인이 없습니다.',
  'domain.recheck': '다시 확인',
  'domain.have': '✓ 내 대표 도메인: <b>{name}</b>',
  'domain.guideTitle': 'Dener 신청에는 dexignation.com 대표 도메인이 필요합니다.',
  'domain.step1': 'dexignation.com에서 도메인을 발급받습니다',
  'domain.step2': '도메인 하나를 대표 도메인으로 지정합니다',
  'domain.step3': '다시 돌아와 Dener 신청을 진행합니다',
  'domain.link': 'dexignation.com 바로가기 ↗',

  // ── distributor address validation ──
  'dist.valid': '✓ 유효한 총판 — 구매자 할인 {disc}% · 총판 보상 {rwd}%',
  'dist.validName': '✓ {name} → {addr} · 구매자 할인 {disc}% · 총판 보상 {rwd}%',
  'dist.inactive': '비활성 총판입니다. 할인 없이 진행됩니다.',
  'dist.notRegistered': '등록되지 않은 총판입니다. 할인 없이 진행됩니다.',
  'dist.required': '유효한 .dex 도메인을 입력해야 서임을 진행할 수 있습니다.',
  'dist.requiredBadge': '필수',
  'dist.requiredHint': '할인 .dex 도메인은 필수입니다.',
  'dist.notDexName': '.dex 이름을 입력하세요 (예: alice.dex)',
  'dist.resolving': '이름 조회 중…',
  'dist.nameNotFound': '등록되지 않은 이름입니다.',
  'dist.noBnbAddr': '이 이름에 연결된 BNB 주소가 없습니다.',
  'dist.resolveError': '이름 조회 중 오류가 발생했습니다. 잠시 후 다시 시도하세요.',
  'dist.loadingDistributors': '총판 목록을 불러오는 중…',
  'dist.selectPlaceholder': '총판을 선택하세요',
  'dist.selectRequired': '총판 선택은 필수입니다.',
  'dist.selectedDistributor': '✓ {name} · 데너 할인 {disc}% · 총판 보상 {rwd}%',
  'dist.selectedInactiveDistributor': '비활성 총판입니다: {name}. 할인 없이 진행됩니다.',
  'dist.selectInvalid': '선택한 총판을 사용할 수 없습니다.',
  'dist.noDistributors': '등록된 총판이 없습니다.',
  'dist.listError': '총판 목록을 불러오지 못했습니다. 잠시 후 다시 시도하세요.',

  // ── distributor dashboard ──
  'dener.title': '내 <em>총판</em> 대시보드',
  'dener.subtitle': 'DISTRIBUTOR DASHBOARD',
  'dener.discLabel': '구매자 할인',
  'dener.rwdLabel': '내 보상',
  'dener.statusLabel': '상태',
  'dener.setLabel': '내 할인율 설정',
  'dener.setOpt': '(0~15%, 보상은 자동)',
  'dener.apply': '할인율 적용 (서명)',
  'dener.lockedText': '🔒 관리자가 할인율/보상율을 설정했습니다. 변경하려면 관리자에게 문의하세요.',
  'dener.buyersHead': '나를 총판으로 지정한 구매자',
  'dener.active': '활성',
  'dener.inactive': '비활성',
  'dener.autoHint': '→ 내 보상 {n}%',
  'dener.signConfirm': '서명을 확인하세요…',
  'dener.applied': '✓ 할인율 {pct}% 적용됨',
  'dener.buyerCount': '{n}명',
  'dener.noBuyers': '아직 나를 통해 구매한 사람이 없습니다.',
  'dener.buyersFail': '조회 실패 (RPC 제한일 수 있음)',

  // ── admin overlay ──
  'admin.title': '관리자 — <em>총판 관리</em>',
  'admin.subtitle': 'OWNER ADMIN',
  'admin.regPlaceholder': '총판 .dex 이름 또는 지갑 주소',
  'admin.regBtn': '+ 등록',
  'admin.regRequired': '등록할 총판 .dex 이름 또는 지갑 주소를 입력하세요.',
  'admin.regInvalid': '.dex 이름 또는 0x 지갑 주소를 입력하세요.',
  'admin.regAddressOk': '✓ 지갑 주소 확인됨: {addr}',
  'admin.regResolved': '✓ {name} → BNB {addr}',
  'admin.detailEmpty': '← 총판을 선택하세요',
  'admin.dBuyersK': '구매자 수',
  'admin.dDiscK': '할인',
  'admin.dRwdK': '보상',
  'admin.activeTitle': '총판 활성화',
  'admin.activeDescDefault': '활성화하면 이 총판의 할인·보상이 적용됩니다',
  'admin.lockTitle': '관리자 잠금',
  'admin.lockDescDefault': '잠금: 관리자만 설정 / 해제: 총판 본인이 설정',
  'admin.discRangeLabel': '데너(Dener) 할인율',
  'admin.rwdRangeLabel': '총판 보상율',
  'admin.apply': '이 할인율·보상율로 설정 (관리자 잠금)',
  'admin.remove': '총판 삭제',
  'admin.noDistributors': '등록된 총판이 없습니다.<br>위에서 .dex 이름 또는 지갑 주소로 등록하세요.',
  'admin.listRates': '할인 {disc}% · 보상 {rwd}% · <span class="{cls}">{status}</span>',
  'admin.activeDescOn': '활성 상태 — 할인·보상이 적용됩니다',
  'admin.activeDescOff': '비활성 상태 — 할인·보상이 적용 안 됩니다',
  'admin.lockDescOn': '잠김 — 관리자만 할인율 설정 가능',
  'admin.lockDescOff': '해제됨 — 총판 본인이 할인율 설정 가능',
  'admin.regSign': '등록 서명을 확인하세요…',
  'admin.registered': '✓ 등록됨',
  'admin.setSign': '설정 서명을 확인하세요…',
  'admin.ratesSet': '✓ 설정됨 (잠금)',
  'admin.toggleSign': '서명 확인…',
  'admin.activated': '활성화',
  'admin.deactivated': '비활성화',
  'admin.toggleResult': '✓ {state}됨',
  'admin.lockApplied': '✓ 관리자 잠금 (할인 {disc}% / 보상 {rwd}%)',
  'admin.released': '✓ 총판 자율로 전환',
  'admin.removeSign': '삭제 서명 확인…',
  'admin.removed': '✓ 삭제됨',

  // ── footer ──
  'footer.brand': 'Dig<em>Mol</em> — Dener 서임의 광맥',
  'footer.line1': '엘도라시아 연대기 · PV 서임 전용 격리 교환 컨트랙트',
  'footer.tag': 'DigMolPvGate · 독립 배포 · 메인 생태계와 무관',
  'footer.disc': '본 페이지의 모든 정보는 참고용이며 투자 권유가 아닙니다. © 2026 MolePin Community',

  // ── contract / refresh ──
  'contract.noAddr': '⚠ PV_GATE_ADDRESS 미설정 — .env 에 VITE_PV_GATE_ADDRESS 를 넣으면 실제 교환이 동작합니다.',
  'contract.vein': '광맥 {link}',
  'refresh.rateNote': 'MOL {mol} · BNB {bnb} · 남은 광맥 {stock} MOL',
  'refresh.queryFail': '계약 조회 실패: {msg}',

  // ── swap flow ──
  'swap.needAmount': '광맥에 넣을 BNB 수량을 입력하세요.',
  'swap.noAddr': 'PV_GATE_ADDRESS가 설정되지 않았습니다 (배포 주소 입력 필요).',
  'swap.failPrefix': '채굴 실패: {msg}',

  // ── tx states ──
  'tx.signing': '지갑에서 서명을 확인하세요…',
  'tx.pendingMining': '곡괭이가 광맥을 친다… 컨펌 대기',
  'tx.confirming': '블록 컨펌 대기 중…',
  'tx.successDefault': '완료',
  'tx.successMined': '⛏ 채굴 성공 — <b>{got}</b> 캐냈습니다',
  'tx.viewTx': '트랜잭션 보기 ↗',

  // ── error maps ──
  'err.userRejected': '서명이 거부되었습니다.',
  'err.insufficientBnb': 'BNB 잔액이 부족합니다(교환액+가스).',
  'err.insufficientStock': '광맥의 MOL 재고가 부족합니다. 잠시 후 다시 시도하세요.',
  'err.gateClosed': '현재 광맥이 닫혀 있습니다.',
  'err.zeroAmount': '금액이 0입니다.',
  'err.noGateAddr': '컨트랙트 주소가 설정되지 않았습니다.',
  'err.tx.userCancelled': '지갑에서 서명을 취소했습니다.',
  'err.tx.insufficientGas': '가스비(BNB)가 부족합니다. 지갑에 BNB를 채워주세요.',
  'err.tx.notOwner': '권한이 없습니다 (owner 지갑으로 실행하세요).',
  'err.tx.notDist': '등록되지 않은 총판입니다.',
  'err.tx.ownerLocked': '관리자가 잠근 총판이라 변경할 수 없습니다.',
  'err.tx.badDiscount': '할인율 범위를 벗어났습니다 (0~15%).',
  'err.tx.badReward': '보상율 범위를 벗어났습니다 (0~5%).',
  'err.tx.network': '네트워크 오류입니다. 잠시 후 다시 시도하세요.',
  'err.tx.unknown': '처리 중 문제가 발생했습니다. 잠시 후 다시 시도하세요.',

  // ── ticker feed messages ──
  'feed.msg1': '새로운 수호자가 왕국에 합류했습니다',
  'feed.msg2': '한 명의 Dener가 서임을 받았습니다',
  'feed.msg3': '무너진 왕국에 수호자가 깃들었습니다',
  'feed.msg4': 'Eldorasia의 빛이 하나 더 밝아졌습니다',
  'feed.msg5': '곡괭이를 든 수호자가 나타났습니다',

  // ── shared ──
  'common.loading': '불러오는 중…',
  'common.txLink': 'tx ↗',
  'common.queryFail': '조회 실패',
};
